import { IContextualMenuItem, Link, Stack, Text } from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { AppLinkTo } from "global/Application";
import { updateNotifications } from "global/Reducer/dataSlice";
import { ValidateDataAsync } from "global/Validation/DataValidation";
import { EDataValidationContext } from "global/Validation/EDataValicationContext";
import { ECommentStatus, ICommentRouteType, INotification } from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Dispatch } from "redux";
import ValidationItem from "views/BlueRelationship/ValidationDialog/ValidationItem";

export enum ENotificationName {
    TelehealthDeprecatedFields = 'telehealth-deprecated-fields',
    RetroactiveTerminationClaimsRecoveryGeneral = 'retroactive-termination-claims-recoveries-general',
    RetroactiveTerminationClaimsRecoveriesMultiplePercentages = 'retroactive-termination-claims-recoveries-multiple-percentages',
    Sprint30ABATherapyOONCostShare = 'sprint30-aba-therapy-oon-costshare',
    Sprint40 = 'sprint40',
    SNFBenefitPeriodMaximums = 'snf-benefit-period-maximums',
    TherapyMaximums = 'therapy-maximums',
    ContractMaximums = 'contract-maximums',
    PreAuthSection = 'pre-auth-section',
    HomeHealthMaximumVisits = 'home-health-maximum-visits',
    PDNVisitAmounts = 'pdn-visit-amounts',
    TravelAndLodgingMaximums = 'travel-and-lodging-maximums',
    InfertilityMaximums = 'infertility-maximums',
    HearingAidExamsMaximumVisits = 'hearing-aid-exams-maximum-visits',
    ReviewChrioLimits = 'review-chrio-limits',
    CochlearImplantUnitMaximums = 'cochlear-implant-unit-maximums',
    NutritionalCounseling = 'nutritional-counseling',
}

interface IProps extends TComponentConnectedProps {
}

// TODO rename this to Action Items Panel
const ActionItemsPanel: React.FunctionComponent<IProps> = (props) => {
    const [refresh, SetRefresh] = useState(0);

    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();

    const history = useHistory();
    const [messages, setMessages] = useState<IValidationMessage[]>([]);

    useEffect(() => {
        (async () => {
            const messages: IValidationMessage[] = [];

            // Validation
            // ------------------------------------------
            const dataValidationResult = await ValidateDataAsync(
                state,
                {
                    checkRef: true,
                    checkRelease: true,
                },
                EDataValidationContext.EActionItem,
                history,
                {},
                null as any,
            );

            if (dataValidationResult.refValidations.length > 0) {
                messages.push({
                    title: `Some elements are referencing a resource that has been deactivated or no longer exists.`,
                    description: `The occurrence of such a situation may arise when a resource undergoes deactivation during the course of business processing or fails to be selected for retention in the subsequent iteration of the merge process. In such cases, it is imperative to reassign or clear each individual entry.`,
                    type: EValidationSeverity.error,
                    inner: dataValidationResult.refValidations
                });
            }

            if (dataValidationResult.uniqueEntityNameValidations.length > 0) {
                const t = {
                    title: `Some elements are sharing an identifier within the same context.`,
                    description: `The following elements have been flagged due to naming errors, necessitating the assignment of unique names. To make the necessary changes, please navigate to the Properties section.\nHere is the list of impacted elements:`,
                    type: EValidationSeverity.info,
                    inner: dataValidationResult.uniqueEntityNameValidations
                };

                messages.push(t);

                if (dataValidationResult.uniqueEntityNameValidations.some(t => t.type === EValidationSeverity.error)) {
                    t.type = EValidationSeverity.error;
                }
            }

            if (dataValidationResult.releaseValidations.length > 0) {
                messages.push(...dataValidationResult.releaseValidations);
            }

            // Notifications
            // ------------------------------------------
            state.data.json.notifications?.forEach(eachNotification => {

                // skip deactivated records/only show deactivated records if permitted at group config properties level
                // displaying inactivated ones
                if ((eachNotification.deactivated ?? false) !== state.data.json.showInActives?.value)
                    return;

                const notificationMessage: IValidationMessage = {
                    title: eachNotification.title ?? 'Action Item',
                    type: EValidationSeverity.info,
                    onRenderDescription: () => {
                        return createRenderDescription(dispatch, null, eachNotification);
                    }
                }

                // TODO move if conditions to switch
                if (eachNotification.name === ENotificationName.TelehealthDeprecatedFields) {
                    const benefit = state.data.json.benefits.find(t => t.id === eachNotification.location?.benefitId);
                    notificationMessage.title = `Important Update on Telehealth Benefit (${benefit?.name})`;
                    notificationMessage.onRenderDescription = () => {
                        return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                            <Stack.Item>
                                <Text>
                                    Please be advised that attributes in the GENERAL MEDICINE / URGENT CARE, BEHAVIORAL HEALTH / MENTAL HEALTH, DERMATOLOGY, and VIRTUAL PRIMARY CARE sections of the Telehealth Benefit have been deprecated and replaced with a single costshare field.
                                    <br />
                                    Make sure to review your Telehealth Benefits and make any necessary updates accordingly.
                                </Text>
                            </Stack.Item>
                            <Stack.Item align="end">
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>

                                    {benefit &&
                                        <Text variant="small">
                                            <Link onClick={() => {
                                                history.push(AppLinkTo(state,
                                                    {
                                                        id: benefit.id,
                                                        type: 'benefit',
                                                    }
                                                ))
                                            }}>
                                                Benefit ({benefit.name})
                                            </Link>
                                        </Text>
                                    }

                                    <Text variant="small">
                                        <Link onClick={() => {
                                            dispatch(updateNotifications([{
                                                id: eachNotification.id,
                                                deactivated: !eachNotification.deactivated
                                            }]));
                                        }}>
                                            {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                        </Link>
                                    </Text>
                                </Stack>

                            </Stack.Item>
                        </Stack>
                    }
                }

                // TODO move if conditions to switch
                if (eachNotification.name === ENotificationName.RetroactiveTerminationClaimsRecoveryGeneral) {
                    notificationMessage.title = `Update: Retroactive Termination Claims Recovery Relocated to Shared Savings Line`;
                    notificationMessage.onRenderDescription = () => {
                        return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                            <Stack.Item>
                                <Text>
                                    Retroactive Termination Claims Recovery Percentage on the Accounts Receivable entity has been deprecated and the value has been moved to a Shared Savings line on the related Fees and Premium record.  Make sure to review the new line for correctness.
                                </Text>
                            </Stack.Item>
                            <Stack.Item align="end">
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>

                                    <Text variant="small">
                                        <Link onClick={() => {
                                            dispatch(updateNotifications([{
                                                id: eachNotification.id,
                                                deactivated: !eachNotification.deactivated
                                            }]));
                                        }}>
                                            {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                        </Link>
                                    </Text>
                                </Stack>

                            </Stack.Item>
                        </Stack>
                    }
                }

                // TODO move if conditions to switch
                if (eachNotification.name === ENotificationName.RetroactiveTerminationClaimsRecoveriesMultiplePercentages) {
                    const feesAndPremium = state.data.json.feesAndPremiums.find(t => t.id === eachNotification.location?.feesAndPremiumId);

                    notificationMessage.title = `Update: Multiple Shared Savings lines added to Fees and Premium.`;
                    notificationMessage.type = EValidationSeverity.warn;
                    notificationMessage.onRenderDescription = () => {
                        return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                            <Stack.Item>
                                <Stack>
                                    <Text>
                                        Multiple Retroactive Termination Claims Recovery lines have been added to the Fees and Premiums ({feesAndPremium?.name}) Shared Savings.
                                    </Text>
                                    <Text>
                                        We were expecting only a single line, but conflicting values were found in the source Accounts Receivable. As a result, we added them as new lines to the Shared Savings attribute set.
                                    </Text>
                                    <Text>
                                        Please review the new lines for correctness.
                                    </Text>
                                </Stack>
                            </Stack.Item>
                            <Stack.Item align="end">
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    {feesAndPremium &&
                                        <Text variant="small">
                                            <Link onClick={() => {
                                                history.push(AppLinkTo(state,
                                                    {
                                                        id: feesAndPremium.id,
                                                        type: 'feesAndPremium',
                                                    }
                                                ))
                                            }}>
                                                Fees And Premium ({feesAndPremium.name})
                                            </Link>
                                        </Text>
                                    }

                                    <Text variant="small">
                                        <Link onClick={() => {
                                            dispatch(updateNotifications([{
                                                id: eachNotification.id,
                                                deactivated: !eachNotification.deactivated
                                            }]));
                                        }}>
                                            {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                        </Link>
                                    </Text>
                                </Stack>

                            </Stack.Item>
                        </Stack>
                    }
                }

                // TODO move if conditions to switch
                if (eachNotification.name === ENotificationName.Sprint30ABATherapyOONCostShare) {
                    notificationMessage.title = `'ABA Therapy' has been consolidated as 'ABA Therapy - Out of Network' - please view the Mental Health Tab for updates.`;
                    notificationMessage.type = EValidationSeverity.info;
                    notificationMessage.onRenderDescription = () => {
                        const description = `The 'ABA Therapy' benefit has been consolidated into the 'ABA Therapy - Out of Network' benefit. Please review the Mental Health Tab for updates.`;
                        return createRenderDescription(dispatch, description, eachNotification);
                    }
                }

                switch (eachNotification.name) {

                    case ENotificationName.TelehealthDeprecatedFields:
                        // TODO move if conditions to switch
                        break;
                    case ENotificationName.RetroactiveTerminationClaimsRecoveryGeneral:
                        // TODO move if conditions to switch
                        break;
                    case ENotificationName.RetroactiveTerminationClaimsRecoveriesMultiplePercentages:
                        // TODO move if conditions to switch
                        break;
                    case ENotificationName.Sprint30ABATherapyOONCostShare:
                        // TODO move if conditions to switch
                        break;

                    case ENotificationName.SNFBenefitPeriodMaximums:
                        notificationMessage.title = `Review SNF Benefit Period Maximums for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.TherapyMaximums:
                        notificationMessage.title = `Data re-entry is required for Therapy Maximums`;
                        notificationMessage.type = EValidationSeverity.warn;
                        notificationMessage.onRenderDescription = () => {
                            const description = `Please navigate to the new section and update accordingly.`;
                            return createRenderDescription(dispatch, description, eachNotification);
                        }
                        break;

                    case ENotificationName.ContractMaximums:
                        notificationMessage.title = `Review Contract Maximums for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.PreAuthSection:
                        notificationMessage.title = `Data re-entry is required for the Pre Auth section`;
                        notificationMessage.type = EValidationSeverity.warn;
                        notificationMessage.onRenderDescription = () => {
                            const description = `Please navigate to the Pre Auth section and update accordingly.`;
                            return createRenderDescription(dispatch, description, eachNotification);
                        }
                        break;

                    case ENotificationName.HomeHealthMaximumVisits:
                        notificationMessage.title = `Review Home Health Maximum Visits for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.PDNVisitAmounts:
                        notificationMessage.title = `Review PDN Visit Amounts for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.TravelAndLodgingMaximums:
                        notificationMessage.title = `Review Travel and Lodging Maximums for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.InfertilityMaximums:
                        notificationMessage.title = `Review Infertility Maximums for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.HearingAidExamsMaximumVisits:
                        notificationMessage.title = `Review Hearing Aid Exams Maximum Visits for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.ReviewChrioLimits:
                        notificationMessage.title = `Review Chiropractic Limits for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.CochlearImplantUnitMaximums:
                        notificationMessage.title = `Review Cochlear Implant Unit Maximums for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    case ENotificationName.NutritionalCounseling:
                        notificationMessage.title = `Review Nutritional Counseling for accuracy`;
                        notificationMessage.type = EValidationSeverity.warn;
                        break;

                    default:
                        break;

                }

                messages.push(notificationMessage);

            })

            state.data.json.comments?.forEach(eachComment => {

                if ((eachComment.deactivated ?? false) !== state.data.json.showInActives?.value)
                    return;

                const isForGroupConfiguration = eachComment.location.type === ICommentRouteType.root &&
                    !eachComment.location.next;

                if (isForGroupConfiguration && eachComment.status !== ECommentStatus.Resolved) {
                    const commentMessage: IValidationMessage = {
                        title: eachComment.comment ?? '?',
                        type: EValidationSeverity.warn,
                    };
                    messages.push(commentMessage);
                }
            });

            setMessages(messages);
        })();

    }, [refresh, state.data.json.notifications, state.data.json.showInActives?.value, state.data.json.comments]);

    const menu: IContextualMenuItem[] = [];
    menu.push({
        key: 'refresh',
        name: 'Refresh',
        icon: 'Refresh',
        onClick: (event, item) => {
            SetRefresh(t => t + 1);
        },
    });

    if (messages.length < 1)
        return <></>;

    return <CustomPanel title={"Action Items"}
        canCollapse={false}
        defaultCollapsed={false}
        contentStyle={{ overflow: 'visible' }}
        storageId={"validationPanel"}
        items={menu}
    >
        <Stack styles={{ root: { marginTop: '10px' } }}>
            {messages.map((msg, ix) => {
                return <ValidationItem key={ix} data={msg} />;
            })}
        </Stack>
    </CustomPanel>;
}

function createRenderDescription(dispatch: Dispatch<any>, description: string | null | React.ReactElement, notification: INotification) {
    return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
        <Stack.Item>
            <Stack>
                <Text>
                    {description}
                </Text>
            </Stack>
        </Stack.Item>
        <Stack.Item align="end">
            <Stack horizontal tokens={{ childrenGap: '10px' }}>
                <Text variant="small">
                    <Link onClick={() => {
                        dispatch(updateNotifications([{
                            id: notification.id,
                            deactivated: !notification.deactivated
                        }]));
                    }}>
                        {notification.deactivated ? "Activate" : "Deactivate"}
                    </Link>
                </Text>
            </Stack>
        </Stack.Item>
    </Stack>;
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(ActionItemsPanel)

